import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, ImagePlus, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "rccd_admin_token";

// Local-only stable key. Stripped before sending to backend so we don't pollute
// the saved JSON. Lets React track each row across reorder/delete operations
// without confusing inputs or expanded state.
const KEY_FIELD = "__key";
let keySeed = 0;
const withKey = (obj) => ({ ...obj, [KEY_FIELD]: obj[KEY_FIELD] ?? `k${Date.now()}-${++keySeed}` });
const stripKeys = (arr) => arr.map(({ [KEY_FIELD]: _k, ...rest }) => rest);

const apiCall = async (path, options = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Request failed");
  return res.status === 204 ? null : res.json();
};

// Schemas for each content type — drives the form fields
const SCHEMAS = {
  projects: {
    label: "Projects (Recent Work)",
    description: "Recent Projects displayed on the Our Work page. Drag to reorder.",
    itemLabel: "Project",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "location", label: "Location", type: "text" },
      { key: "category", label: "Category", type: "text", placeholder: "Residential / Master-Planned / Entitlement" },
      { key: "acres", label: "Acres", type: "text" },
      { key: "lots", label: "Lots / units", type: "text" },
      { key: "year", label: "Year / status", type: "text", placeholder: "e.g. Approved 2014" },
      { key: "blurb", label: "Description", type: "textarea" },
      { key: "image", label: "Image", type: "image" },
    ],
    blank: () => ({ title: "", location: "", category: "Residential", acres: "", lots: "", year: "", blurb: "", image: "" }),
  },
  team: {
    label: "Team Members",
    description: "People shown on the Meet the Team page. Order matters.",
    itemLabel: "Person",
    fields: [
      { key: "name", label: "Full name", type: "text", required: true },
      { key: "role", label: "Role / title", type: "text", required: true },
      { key: "bio", label: "Short bio", type: "textarea" },
      { key: "initials", label: "Initials (for avatar)", type: "text", placeholder: "e.g. AR" },
      { key: "confirmed", label: "Show on public site (uncheck for draft)", type: "boolean" },
      { key: "image", label: "Headshot", type: "image" },
    ],
    blank: () => ({ name: "", role: "", bio: "", initials: "", confirmed: true, image: "" }),
  },
  faqs: {
    label: "FAQ Items",
    description: "Frequently asked questions on the Contact page.",
    itemLabel: "Question",
    fields: [
      { key: "q", label: "Question", type: "text", required: true },
      { key: "a", label: "Answer", type: "textarea", required: true },
    ],
    blank: () => ({ q: "", a: "" }),
  },
  stats: {
    label: "Hero Stats",
    description: "The four big numbers on the home page.",
    itemLabel: "Stat",
    fields: [
      { key: "value", label: "Numeric value", type: "number", required: true },
      { key: "prefix", label: "Prefix", type: "text", placeholder: "$ (optional)" },
      { key: "suffix", label: "Suffix", type: "text", placeholder: "+ or M+" },
      { key: "label", label: "Label", type: "text", required: true },
      { key: "desc", label: "Description", type: "text" },
    ],
    blank: () => ({ value: 0, prefix: "", suffix: "+", label: "", desc: "" }),
  },
  partners: {
    label: "Partner Logos",
    description: "Logos in the scrolling marquee on the home page.",
    itemLabel: "Logo",
    fields: [
      { key: "name", label: "Brand name (internal)", type: "text" },
      { key: "image", label: "Logo (PNG/SVG/JPG)", type: "image", required: true },
    ],
    blank: () => ({ name: "", image: "" }),
  },
  copy: {
    label: "Site Copy",
    description: "Headlines and paragraphs across the site.",
    singleton: true,
    fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow", type: "text" },
      { key: "hero_headline", label: "Hero headline", type: "textarea" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "about_headline", label: "About headline", type: "textarea" },
      { key: "about_paragraph_1", label: "About paragraph 1", type: "textarea" },
      { key: "about_paragraph_2", label: "About paragraph 2", type: "textarea" },
      { key: "contact_headline", label: "Contact section headline", type: "text" },
      { key: "contact_subtitle", label: "Contact section subtitle", type: "textarea" },
      { key: "footer_tagline", label: "Footer tagline", type: "text" },
    ],
  },
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const ImageField = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) { toast.error("Max 5MB image"); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</Label>
      <div className="mt-2 flex items-center gap-4">
        {value && <img src={value} alt="" className="h-16 w-16 object-cover border border-border bg-secondary" />}
        <label className="inline-flex items-center gap-2 cursor-pointer bg-secondary border border-border px-4 h-10 mono text-[10px] uppercase tracking-[0.22em] hover:border-clay transition-colors">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          <span>{value ? "Replace" : "Upload"}</span>
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </label>
        {value && (
          <button onClick={() => onChange("")} className="mono text-[10px] uppercase tracking-[0.22em] text-destructive hover:underline">Remove</button>
        )}
      </div>
    </div>
  );
};

const Field = ({ field, value, onChange }) => {
  if (field.type === "image") return <ImageField label={field.label} value={value || ""} onChange={onChange} />;
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-clay" />
        <span className="text-sm text-foreground/85">{field.label}</span>
      </label>
    );
  }
  return (
    <div>
      <Label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{field.label}{field.required && " *"}</Label>
      {field.type === "textarea" ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={field.placeholder}
          className="mt-2 rounded-none border-border bg-card focus-visible:ring-0 focus-visible:border-clay"
        />
      ) : (
        <Input
          type={field.type === "number" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
          placeholder={field.placeholder}
          className="mt-2 h-11 rounded-none border-border bg-card focus-visible:ring-0 focus-visible:border-clay"
        />
      )}
    </div>
  );
};

export const ContentEditor = ({ slug }) => {
  const schema = SCHEMAS[slug];
  const [items, setItems] = useState([]);
  const [singleton, setSingleton] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall(`/api/content/${slug}`);
      if (schema.singleton) {
        setSingleton(res?.data || {});
      } else {
        setItems(Array.isArray(res?.data) ? res.data.map(withKey) : []);
      }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [slug, schema.singleton]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const data = schema.singleton ? singleton : stripKeys(items);
      await apiCall(`/api/admin/content/${slug}`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      });
      toast.success("Saved. Live on public site.");
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  };
  const addItem = () => {
    const blank = schema.blank ? schema.blank() : {};
    setItems(prev => [...prev, withKey(blank)]);
    setExpandedIdx(items.length);
  };
  const removeItem = (idx) => {
    if (!confirm(`Delete this ${schema.itemLabel.toLowerCase()}?`)) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
    setExpandedIdx(null);
  };
  const move = (idx, dir) => {
    setItems(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...</div>;

  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-6 flex-wrap">
        <div>
          <h2 className="font-display text-3xl text-ink tracking-[-0.01em]">{schema.label}</h2>
          <p className="text-sm text-muted-foreground mt-2">{schema.description}</p>
        </div>
        <Button onClick={save} disabled={saving} className="h-11 rounded-none bg-clay text-paper hover:bg-primary-glow mono text-[11px] uppercase tracking-[0.22em]" data-testid="save-content-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save changes
        </Button>
      </div>

      {schema.singleton ? (
        <div className="bg-card border border-border p-8 space-y-5 max-w-2xl">
          {schema.fields.map(f => (
            <Field key={f.key} field={f} value={singleton[f.key]} onChange={(v) => setSingleton(prev => ({ ...prev, [f.key]: v }))} />
          ))}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={item[KEY_FIELD]} className="bg-card border border-border" data-testid={`${slug}-item-${idx}`}>
                <div className="flex items-center justify-between px-5 py-4 gap-4 cursor-pointer hover:bg-secondary/40" onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}>
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <span className="mono text-[10px] tracking-[0.22em] text-muted-foreground w-6">{String(idx + 1).padStart(2, '0')}</span>
                    {item.image && <img src={item.image} alt="" className="h-10 w-10 object-cover border border-border shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-display text-lg text-ink truncate">{item.title || item.name || item.q || item.label || `${schema.itemLabel} ${idx + 1}`}</div>
                      {item.role && <div className="mono text-[10px] uppercase tracking-[0.22em] text-clay">{item.role}</div>}
                      {item.location && <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{item.location}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.confirmed === false && <span className="mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground border border-dashed border-border px-2 py-1"><EyeOff className="h-3 w-3 inline" /> Draft</span>}
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); move(idx, -1); }} disabled={idx === 0} className="h-8 w-8"><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); move(idx, 1); }} disabled={idx === items.length - 1} className="h-8 w-8"><ArrowDown className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="h-8 w-8 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {expandedIdx === idx && (
                  <div className="border-t border-border p-6 space-y-5 bg-secondary/30">
                    {schema.fields.map(f => (
                      <Field key={f.key} field={f} value={item[f.key]} onChange={(v) => updateItem(idx, f.key, v)} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button onClick={addItem} variant="outline" className="mt-6 rounded-none border-dashed border-border w-full h-12 mono text-[11px] uppercase tracking-[0.22em]" data-testid={`add-${slug}-btn`}>
            <Plus className="h-4 w-4 mr-2" /> Add {schema.itemLabel}
          </Button>
        </>
      )}
    </div>
  );
};
